import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format as formatDate } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PROCESSING_STATUS_COLOR_CLASS } from '../../../constants.ts';
import { ERROR_RESOLUTIONS, useSteprunInfo } from '../../../../core/utils/steprunConfig.ts';
import { url } from '../../../../core/utils/link.ts';
import ConfirmModal from '../../../../core/components/ConfirmModal.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import {
    faArrowDownToArc,
    faArrowRotateLeft,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faCircleHalfStroke,
    faDatabase,
    faEnvelope,
    faFileLines,
    faArrowLeft,
    faRectangleVerticalHistory,
    faSpinnerThird,
    faPaperclipVertical,
} from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../../../../core/components/Tooltip.tsx';
import Page from '../../../../core/components/Page';
import { useAssistanceContext } from '../AssistanceContext.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import DropdownMenu from '../../../../core/components/DropdownMenu.tsx';
import { TABS_OPTIONS, useCurrentTabName } from '../tabs.ts';
import { isFeatureEnabled } from '../../../../customizations/framework.tsx';
import TextField from '../../../../core/components/Fields/TextField.tsx';
import { useQuery, useMutation } from '@apollo/client';
import { GET_NAVIGATION_CHANNELS } from '../../../../core/components/Navigation/internal/queries.ts';
import {
    filterAttachments,
    getRecordListPath,
    isRecordInAssistableStatus,
    isRecordInDiscardableStatus,
} from '../../../utils.ts';
import {
    getUserFilterStorageKeyFromRecord,
    PAGE_KEY,
    PAGE_SIZE_VALUE_DEFAULT,
    useUserFilters,
} from '../../../../core/utils/filterQuery.ts';
import AssignToDropdown, { ChannelUser } from '../../Overview/internal/AssignToDropdown.tsx';
import { ASSIGN_TO_DOCUMENTS } from '../queries';
import { mapDocumentTypeToGqlDocumentType } from '../../../constants';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { camelCase } from 'lodash';
import { capitalizeFirstLetter, titleCase, toHumanReadable } from '../../../../core/utils/string.ts';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';

const ChevronDownIcon = withIcon(faChevronDown);
const EmailsIcon = withIcon(faEnvelope);
const AttachmentIcon = withIcon(faPaperclipVertical);
const ResetChangesIcon = withIcon(faArrowRotateLeft);
const OriginalDataIcon = withIcon(faCircleHalfStroke);
const SourceIcon = withIcon(faArrowDownToArc);
const DocumentIcon = withIcon(faFileLines);
const MasterDataIcon = withIcon(faDatabase);
const ChevronLeftIcon = withIcon(faChevronLeft);
const ChevronRightIcon = withIcon(faChevronRight);
const LoadingIcon = withIcon(faSpinnerThird);
const OutputFileIcon = withIcon(faRectangleVerticalHistory);
const BackIcon = withIcon(faArrowLeft);

const StatusIndicator = ({ status, className }: any) => {
    const statusColorGroup = PROCESSING_STATUS_COLOR_CLASS[status];
    return (
        <div
            className={classnames(
                'rounded-full w-2.5 h-2.5 bg-line-strong flex-none',
                statusColorGroup == 'success' && 'bg-surface-success-strong',
                statusColorGroup == 'warning' && 'bg-surface-warning-strong',
                statusColorGroup == 'error' && 'bg-surface-danger-strong',
                className
            )}
        />
    );
};

const DropdownMenuStatusItem = ({ status, active = false, className, ...props }: any) => {
    const { t } = useTranslation('assistance');
    const statusColorGroup = PROCESSING_STATUS_COLOR_CLASS[status];

    return (
        <DropdownMenu.Item
            {...props}
            className={classnames(
                'hover:bg-surface-strong hover:text-content-strong',
                'data-[disabled]:opacity-100', // Force overwrite default dropdown item disabled style
                active && 'bg-surface-strong text-content-strong',
                statusColorGroup == 'success' &&
                    'hover:bg-surface-success-weak hover:text-content-success-strong active:bg-surface-success-weak active:text-content-success-strong focus:bg-surface-success-weak focus:text-content-success-strong',
                active && statusColorGroup == 'success' && 'bg-surface-success-weak text-content-success-strong',
                statusColorGroup == 'warning' &&
                    'hover:bg-surface-warning-weak hover:text-content-warning-strong active:bg-surface-warning-weak active:text-content-warning-strong focus:bg-surface-warning-weak focus:text-content-warning-strong',
                active && statusColorGroup == 'warning' && 'bg-surface-warning-weak text-content-warning-strong',
                statusColorGroup == 'error' &&
                    'hover:bg-surface-danger-weak hover:text-content-danger-strong active:bg-surface-danger-weak active:text-content-danger-strong focus:bg-surface-danger-weak focus:text-content-danger-strong',
                active && statusColorGroup == 'error' && 'bg-surface-danger-weak text-content-danger-strong',
                className
            )}
        >
            <div className="flex items-center gap-2">
                <StatusIndicator status={status} />
                <span className="text-content-strong">{t(`assistance:processingStatus.${status}`)}</span>
            </div>
        </DropdownMenu.Item>
    );
};

export const AssistanceHeaderTitleGroup = () => {
    const { record, handlers, loading, documentConfiguration, refetchData } = useAssistanceContext();
    const { t } = useTranslation('assistance');

    // Take the EMailSubject only or the file name if uploaded via WB
    const title = record?.sourceFile?.subject || record?.inputFile?.name || record?.subject || '';
    const status = record?.status;

    // On Hold stuff
    const isAssistableStatus = isRecordInAssistableStatus(status);
    const isDiscardableStatus = isRecordInDiscardableStatus(status);
    const [onHoldModalVisible, setOnHoldModalVisible] = useState(false);
    const handleOnHoldConfirm = useCallback(
        (reason) => {
            return handlers?.onChangeStatus?.('ON_HOLD', reason).then(() => setOnHoldModalVisible(false));
        },
        [handlers?.onChangeStatus, record]
    );
    const handleOnHold = useCallback(() => setOnHoldModalVisible(true), []);
    const handleOnHoldReject = useCallback(() => setOnHoldModalVisible(false), []);

    // render helper
    const titleRef = useRef(null);

    const { data: existingChannelsData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
    });

    const documentName = capitalizeFirstLetter(documentConfiguration.documentTypeName);
    const { data: channelsData } = useQuery(documentConfiguration.GET_CHANNELS_OVERVIEW_DATA, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
    });
    const channels = channelsData?.[camelCase(documentName) + 'ProcessingChannels'] || [];
    const activeChannel = channels?.find((channel) => channel.id === record?.channel?.id);
    const { user } = useApplicationContext();

    const channelUsers: ChannelUser[] = useMemo(() => {
        const users: ChannelUser[] = activeChannel?.team?.users || [];
        const sortedUsers = [...users].sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
        sortedUsers.unshift(user);
        return sortedUsers;
    }, [activeChannel]);

    const [assignToDocuments] = useMutation(ASSIGN_TO_DOCUMENTS);

    const isMoveToAnotherChannelDisabled = Object.values(existingChannelsData ?? []).flat().length < 2;
    /*
        Since we cannot rely on the same logic used on the backend
        (where we check for null values of fields like "finished_at", "discard_record", and "deleted_at" in the query builder),
        we can only determine the finished state based on the record's status.
    */
    const isInactive = ['FINISHED', 'DELIVERED', 'IMPORTED', 'DISCARDED', 'DELETED'].includes(status);
    const isMoveToAnotherChannelVisible = !isInactive && !record?.isTestDocument;

    const { publishToast } = useToaster();

    const handleAssignUser = (emailAddress: string | null, user?: { firstName: string; lastName: string } | null) => {
        return assignToDocuments({
            variables: {
                documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
                recordIds: [record.id],
                emailAddress: emailAddress,
            },
            fetchPolicy: 'no-cache',
        })
            .then(() => {
                if (emailAddress === null) {
                    publishToast({
                        description: t('header.assignToUnassignSuccess'),
                        status: 'success',
                    });
                } else if (user) {
                    publishToast({
                        description: t('header.assignToSuccess', {
                            firstName: user.firstName,
                            lastName: user.lastName,
                        }),
                        status: 'success',
                    });
                }
                void refetchData();
            })
            .catch(() => publishToast({ description: t('header.error.assignTo'), status: 'error' }));
    };
    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <button
                        className="flex flex-shrink min-w-0 overflow-hidden gap-3 rounded transition-colors hover:bg-surface-strong py-1.5 px-3 items-center cursor-pointer active:bg-surface-brand-medium-default data-[state=open]:bg-surface-brand-medium-default select-text"
                        data-e2e="assistance-header-title"
                    >
                        <StatusIndicator status={status} />
                        <Tooltip
                            className="flex-shrink min-w-0"
                            content={title}
                            triggerRef={titleRef}
                            overflowOnly
                            long
                        >
                            <h2
                                className="font-medium text-base text-content-strong truncate flex-shrink min-w-0 force-line-height"
                                ref={titleRef}
                            >
                                {title}
                            </h2>
                        </Tooltip>
                        {record?.isTestDocument ? (
                            <div className="rounded flex-none leading-none text-[0.7rem] py-1 px-1.5 uppercase font-bold text-content-white dark:text-content-black bg-surface-brand-strong-default">
                                {t('header.testDocument')}
                            </div>
                        ) : null}
                        {loading ? (
                            <LoadingIcon className="text-content-brand-strong text-sm animate-spin" />
                        ) : (
                            <ChevronDownIcon className="text-content-strong text-sm" />
                        )}
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start">
                    {isAssistableStatus ? (
                        <>
                            <DropdownMenuStatusItem
                                status="ASSISTANCE_NEEDED"
                                active={status === 'ASSISTANCE_NEEDED'}
                                onSelect={
                                    status !== 'ASSISTANCE_NEEDED'
                                        ? () => handlers?.onChangeStatus?.('ASSISTANCE_NEEDED')
                                        : undefined
                                }
                            />
                            <DropdownMenuStatusItem
                                status="ON_HOLD"
                                active={status === 'ON_HOLD'}
                                onSelect={status !== 'ON_HOLD' ? handleOnHold : undefined}
                            />
                        </>
                    ) : (
                        <DropdownMenuStatusItem status={status} active />
                    )}

                    <DropdownMenu.Separator />

                    {!isInactive && (
                        <>
                            <DropdownMenu.Sub>
                                <DropdownMenu.SubTrigger className="outline-none pl-3 hover:cursor-pointer">
                                    {t('overview.recordRow.actions.assignTo')}
                                </DropdownMenu.SubTrigger>
                                <DropdownMenu.SubContent className="!max-h-[20rem] !p-0 flex flex-col ml-[8px] -mt-[7px]">
                                    <AssignToDropdown
                                        removeSelected={false}
                                        selectedUser={record?.assignedTo?.email}
                                        onSelectedUserChange={handleAssignUser}
                                        channelUsers={channelUsers}
                                    />
                                </DropdownMenu.SubContent>
                            </DropdownMenu.Sub>
                        </>
                    )}

                    {isMoveToAnotherChannelVisible && (
                        <>
                            <Tooltip
                                // to hide tooltip on condition we can just pass empty string
                                content={
                                    isMoveToAnotherChannelDisabled ? t('header.actions.moveToChannelDisabledHint') : ''
                                }
                                side="top"
                                asChild={!isMoveToAnotherChannelDisabled}
                            >
                                <DropdownMenu.Item
                                    className="hover:cursor-pointer"
                                    disabled={isMoveToAnotherChannelDisabled}
                                    onClick={handlers.onMoveDocumentsOpen}
                                >
                                    {t('header.actions.moveToChannel')}
                                </DropdownMenu.Item>
                            </Tooltip>
                            <DropdownMenu.Separator />
                        </>
                    )}

                    <DropdownMenu.Item
                        className="hover:cursor-pointer"
                        onSelect={handlers.onDiscardOpen}
                        disabled={!isDiscardableStatus}
                    >
                        {t('header.actions.discardDocument')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        className="!text-content-danger-strong hover:bg-surface-danger-weak hover:cursor-pointer focus:bg-surface-danger-weak"
                        onSelect={handlers?.onDeleteDocumentOpen}
                    >
                        {t('header.actions.deleteDocument')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>

            <OnHoldModal visible={onHoldModalVisible} onConfirm={handleOnHoldConfirm} onCancel={handleOnHoldReject} />
        </>
    );
};

export const AssistanceHeaderViewsToggle = ({ visibleTabs }: { visibleTabs?: TABS_OPTIONS[] | undefined }) => {
    const { t } = useTranslation('assistance');
    const { recordId } = useParams();
    const { record, tabs, documentConfiguration } = useAssistanceContext();

    // move
    const currentTab = useCurrentTabName();
    const tabNames = tabs ? Object.keys(tabs) : [];
    visibleTabs = visibleTabs !== undefined ? visibleTabs : [TABS_OPTIONS.Source, TABS_OPTIONS.Document];

    const attachmentsCount = filterAttachments(record).filter(
        (attachment) => attachment?.file?.type !== 'message/rfc822'
    ).length;

    const filterTabNames = tabNames.filter((tabName) => visibleTabs.includes(tabName as TABS_OPTIONS));

    return (
        <div className="flex gap-1 select-none flex-none items-center">
            {filterTabNames.map((tabName) => (
                <Link
                    key={tabName}
                    className={classnames(
                        'flex items-center gap-2 rounded transition-colors hover:bg-surface-strong py-1.5 px-3 h-[2rem] cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong',
                        tabName == currentTab &&
                            '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                    )}
                    to={url(
                        documentConfiguration.ASSISTANCE_TAB_PATH,
                        { recordId, tab: tabName },
                        { keepSearch: true, excludeSearch: ['mode'] }
                    )}
                >
                    {getTabIcon(tabName as TABS_OPTIONS)}
                    {t(`header.tabs.${tabName}`)}
                </Link>
            ))}
            {filterTabNames.length > 0 && <div className="h-6 mx-3 border-r border-solid border-line-weak" />}
            {isFeatureEnabled('assistanceEmailThreads') && (
                <Link
                    className={classnames(
                        'flex items-center gap-2 rounded transition-colors hover:bg-surface-strong py-1.5 px-3 h-[2rem] cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong',
                        TABS_OPTIONS.Emails == currentTab &&
                            '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                    )}
                    to={url(
                        documentConfiguration.ASSISTANCE_TAB_PATH,
                        { recordId, tab: TABS_OPTIONS.Emails },
                        { keepSearch: true, excludeSearch: ['mode'] }
                    )}
                >
                    <EmailsIcon />

                    {!!record?.emailCount && (
                        <span
                            className={classnames(
                                'font-medium text-xs rounded-full bg-surface-strong px-[0.375rem] min-w-[1.25rem] h-[1.25rem] inline-flex items-center justify-center',
                                TABS_OPTIONS.Emails == currentTab &&
                                    '!bg-surface-brand-medium-default hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                            )}
                        >
                            {record?.emailCount}
                        </span>
                    )}
                </Link>
            )}
            <Tooltip content={attachmentsCount ? t('attachments.showFiles') : t('attachments.noFiles')}>
                {attachmentsCount ? (
                    <Link
                        className={classnames(
                            'flex items-center gap-2 rounded transition-colors hover:bg-surface-strong py-1.5 px-3 h-[2rem] cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong',
                            TABS_OPTIONS.Attachments == currentTab &&
                                '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                        )}
                        to={url(
                            documentConfiguration.ASSISTANCE_TAB_PATH,
                            { recordId, tab: TABS_OPTIONS.Document },
                            { keepSearch: true, search: { mode: 'attachments' } }
                        )}
                    >
                        <AttachmentIcon />

                        <span
                            className={classnames(
                                'font-medium text-xs rounded-full bg-surface-strong px-[0.375rem] min-w-[1.25rem] h-[1.25rem] inline-flex items-center justify-center',
                                TABS_OPTIONS.Attachments == currentTab &&
                                    '!bg-surface-brand-medium-default hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                            )}
                        >
                            {attachmentsCount}
                        </span>
                    </Link>
                ) : (
                    <div className="flex items-center justify-center w-8 h-[2rem]">
                        <AttachmentIcon className={'text-content-weak cursor-pointer'} />
                    </div>
                )}
            </Tooltip>
        </div>
    );
};

const AssistanceHeaderIconButton = ({ className, active = false, ...props }: any) => {
    return (
        <button
            className={classnames(
                'flex items-center justify-center gap-2 rounded transition-colors hover:bg-surface-strong w-8 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-50 disabled:pointer-events-none data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            {...props}
        />
    );
};

const AssistanceHeaderIconLink = ({ className, active = false, disabled, to, ...props }: any) => {
    return (
        <Link
            className={classnames(
                'flex items-center justify-center gap-2 rounded transition-colors hover:bg-surface-strong w-8 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                disabled && 'opacity-50 pointer-events-none',
                className
            )}
            {...props}
            to={to}
        />
    );
};

export const AssistanceHeaderControls = ({
    hideResetButton = false,
    hideOriginalDataButton = false,
    hideMasterDataButton = false,
    hideOutputFileButton = false,
}: any) => {
    const { t } = useTranslation('assistance');
    const { record, handlers, documentConfiguration } = useAssistanceContext();
    const status = record?.status;
    const currentTab = useCurrentTabName();
    const { recordId } = useParams();
    const navigate = useNavigate();
    const [resetModalVisible, setResetModalVisible] = useState(false);

    const hasMasterdata = record?.masterdataVersionsExtracted && record?.masterdataVersionsExtracted?.length > 0;
    const assistableStatus = ['ASSISTANCE_NEEDED', 'ON_HOLD'].includes(status);

    return (
        <>
            <div className="flex gap-1 select-none shrink-0">
                {!hideMasterDataButton && (
                    <Tooltip
                        content={
                            hasMasterdata
                                ? t(`masterdata:versionSummary.tooltip`)
                                : t(`masterdata:versionSummary.tooltipDisabled`)
                        }
                    >
                        <AssistanceHeaderIconButton
                            active={currentTab === TABS_OPTIONS.MasterData}
                            disabled={!hasMasterdata}
                            onClick={() =>
                                navigate(
                                    url(documentConfiguration.ASSISTANCE_TAB_PATH, {
                                        recordId,
                                        tab: TABS_OPTIONS.MasterData,
                                    })
                                )
                            }
                        >
                            <MasterDataIcon />
                        </AssistanceHeaderIconButton>
                    </Tooltip>
                )}
                {!hideOutputFileButton && (
                    <Tooltip content={t('header.showOutputFileStatus')}>
                        <AssistanceHeaderIconButton
                            active={currentTab === TABS_OPTIONS.ProcessingHistory}
                            onClick={() =>
                                navigate(
                                    url(documentConfiguration.ASSISTANCE_TAB_PATH, {
                                        recordId,
                                        tab: TABS_OPTIONS.ProcessingHistory,
                                    })
                                )
                            }
                        >
                            <OutputFileIcon />
                        </AssistanceHeaderIconButton>
                    </Tooltip>
                )}
                <div className="h-6 border-r mt-1 mx-3 border-solid border-line-weak" />
                {!hideOriginalDataButton && (
                    <Tooltip
                        content={
                            handlers?.showOriginalData
                                ? t('header.alerts.executionFinished.showResult')
                                : t('header.alerts.executionFinished.showOriginal')
                        }
                    >
                        <AssistanceHeaderIconButton
                            onClick={() => handlers?.onShowOriginalData(!handlers?.showOriginalData)}
                        >
                            <OriginalDataIcon className={classnames(handlers?.showOriginalData && '-scale-x-100')} />
                        </AssistanceHeaderIconButton>
                    </Tooltip>
                )}
                {!hideResetButton && (
                    <Tooltip content={t('nextButton.resetButton')}>
                        <AssistanceHeaderIconButton
                            onClick={() => setResetModalVisible(true)}
                            disabled={!assistableStatus}
                        >
                            <ResetChangesIcon />
                        </AssistanceHeaderIconButton>
                    </Tooltip>
                )}
            </div>

            <ConfirmModal
                title={t('resetModal.title')}
                labelConfirm={t('resetModal.confirmButton')}
                labelCancel={t('resetModal.cancelButton')}
                onConfirm={() => {
                    setResetModalVisible(false);
                    return handlers?.onReset?.();
                }}
                onCancel={() => setResetModalVisible(false)}
                visible={resetModalVisible}
                danger
            >
                <p>{t('resetModal.message')}</p>
            </ConfirmModal>
        </>
    );
};

export const AssistanceHeaderPagination = () => {
    const { t } = useTranslation('assistance');
    const { documentConfiguration, pagination, record } = useAssistanceContext();
    const { filteredCount, position, nextRecordId, previousRecordId } = pagination || {};

    const initialLoading = !record;

    return (
        <div className="flex gap-2 items-center select-none shrink-0">
            <AssistanceHeaderIconLink
                disabled={initialLoading || position === 1}
                to={
                    previousRecordId
                        ? url(
                              documentConfiguration.ASSISTANCE_PATH,
                              { recordId: previousRecordId },
                              { keepSearch: true }
                          )
                        : ''
                }
            >
                <ChevronLeftIcon />
            </AssistanceHeaderIconLink>

            <div className="flex gap-1 text-sm text-content-strong cursor-default min-w-[38px] text-center justify-center tabular-nums">
                {initialLoading ? (
                    <LoadingIcon className="text-content-brand-strong text-sm animate-spin" />
                ) : (
                    <>
                        <span>{position}</span>
                        <span>{t('header.pagination.of')}</span>
                        <span>{filteredCount}</span>
                    </>
                )}
            </div>

            <AssistanceHeaderIconLink
                disabled={initialLoading || position === filteredCount}
                to={
                    nextRecordId
                        ? url(documentConfiguration.ASSISTANCE_PATH, { recordId: nextRecordId }, { keepSearch: true })
                        : ''
                }
            >
                <ChevronRightIcon />
            </AssistanceHeaderIconLink>
        </div>
    );
};

export const AssistanceAlertBar = () => {
    const { alert } = useAssistanceContext();

    if (!alert) return null;

    return (
        <div
            className={classnames(
                'w-full px-7 py-4 text-sm flex justify-between items-center gap-4 text-content-strong bg-surface-strong border-b border-solid border-line-weak whitespace-pre-line',
                alert?.severity == 'success' && 'bg-surface-success-weak text-content-success-strong',
                alert?.severity == 'warning' && 'bg-surface-warning-weak text-content-warning-strong',
                alert?.severity == 'error' && 'bg-surface-danger-weak text-content-danger-strong'
            )}
        >
            <div className={classnames('flex-auto w-full', alert?.bold && 'font-medium')}>
                {alert?.title && (
                    <span className={classnames('inline-block mr-1', !alert?.bold && 'font-normal')}>
                        {alert?.title}
                    </span>
                )}
                <span>{alert?.message}</span>
            </div>
            <span className="flex-none">{alert?.button}</span>
        </div>
    );
};

export const AssistanceHeaderBackButton = () => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { t } = useTranslation('assistance');

    const { record, documentConfiguration, pagination } = useAssistanceContext();
    const { documentTypeName } = documentConfiguration;

    const [, , pageSize] = useUserFilters({
        storageKey: getUserFilterStorageKeyFromRecord(documentTypeName, record),
        defaultPageSize: PAGE_SIZE_VALUE_DEFAULT,
    });

    const [searchParams] = useSearchParams();
    const channelId = searchParams.get('channel');

    const navigate = useNavigate();

    const testing: boolean = record?.isTestDocument || false;
    const finished: boolean = record?.stepRun?.executionStatus === 'SUCCEEDED' || record?.deletedAt;
    const tabKey = testing ? 'testing' : finished ? 'finished' : 'open';

    const tooltipContent: string = t('header.backTooltip', {
        breadcrumbs: [
            t(`overview.navigation.${documentTypeName}`),
            channelId ? record?.channel?.name : '',
            t(`overview.header.${tabKey}`),
        ]
            .filter(Boolean)
            .join(' / '),
    });

    function onBackButtonClickHandler() {
        const redirectUrl = getRecordListPath(documentConfiguration, { channelId, finished, testing });

        if (!pagination) {
            navigate(redirectUrl);
            return;
        }

        // We need to calculate page number to redirect user back to overview page
        // based on current document position in a queue and page size
        const newPageNumber = Math.trunc(pagination.position / pageSize) + 1;

        if (newPageNumber === 1) {
            navigate(redirectUrl);

            return;
        }

        const parsedUrl = new URL(redirectUrl, window.location.origin);

        parsedUrl.searchParams.set(PAGE_KEY, newPageNumber.toString());
        const urlWithQuery = `${parsedUrl.pathname}?${parsedUrl.searchParams.toString()}`;
        navigate(urlWithQuery);
    }

    return (
        <Tooltip className="flex-shrink min-w-0" content={tooltipContent} triggerRef={buttonRef}>
            <button
                ref={buttonRef}
                onClick={onBackButtonClickHandler}
                className={classnames(
                    'flex items-center justify-center gap-2 rounded transition-colors hover:bg-surface-strong min-w-8 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-50 disabled:pointer-events-none data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong'
                )}
            >
                <BackIcon />
            </button>
        </Tooltip>
    );
};

const AssistanceHeader = ({ visibleTabs }: { visibleTabs?: TABS_OPTIONS[] | undefined }) => {
    return (
        <>
            <div className="flex items-center px-4 py-2 gap-2 md:gap-4 border-b border-solid border-line-weak overflow-x-auto">
                {/* keeps a usable title width on phones so the rest of the header scrolls instead of squashing the title group */}
                <div className="flex row items-center flex-1 min-w-0 max-md:min-w-56">
                    <Page.HeaderNavigationToggle className="mr-2" />
                    <AssistanceHeaderBackButton />
                    <AssistanceHeaderTitleGroup />
                </div>
                <AssistanceHeaderViewsToggle visibleTabs={visibleTabs} />

                <div className="h-6 shrink-0 border-r border-solid border-line-weak" />

                <AssistanceHeaderControls />

                <div className="h-6 shrink-0 border-r border-solid border-line-weak" />

                <AssistanceHeaderPagination />
            </div>

            <AssistanceAlertBar />
        </>
    );
};

export default React.memo(AssistanceHeader);

export const useErrorAlert = (title, message, secondsVisible = 5) => {
    const [alert, setAlert] = useState(undefined);

    useEffect(() => {
        if (!message) return;

        setAlert({ title, severity: 'error', secondsVisible });

        if (!secondsVisible) return;

        const timeout = setTimeout(() => setAlert(undefined), secondsVisible * 1000);
        return () => clearTimeout(timeout);
    }, [message]);

    return alert;
};

export const useAlert = (record, stepRun, locked, lockedBy) => {
    const { t } = useTranslation('assistance');

    const isSucceeded = stepRun?.executionStatus === 'SUCCEEDED';
    const isFailed = stepRun?.executionStatus === 'FAILED';
    const isPending = stepRun?.executionStatus === 'PENDING';
    const isDiscarded = record?.isDiscarded;
    const isDeleted = record?.deletedAt;
    const isManualOnly = stepRun?.manualOnly;
    const isOnHold = record?.status === 'ON_HOLD';
    const isRejected = record?.status === 'REJECTED';
    const isDeliverySucceeded = record?.deliveryRecord?.deliveryStatus === 'SUCCEEDED';
    const isDeliveryFailed = record?.deliveryRecord?.deliveryStatus === 'FAILED';

    const externalReference = record?.deliveryRecord?.externalReference;
    const externalMessage = record?.deliveryRecord?.externalMessage;
    const externalStatus = record?.deliveryRecord?.externalStatus;
    const externalParts = [
        externalReference && `${t('header.alerts.deliveryFailed.externalReference')}: ${externalReference}`,
        externalStatus && `${t('header.alerts.deliveryFailed.externalStatus')}: ${externalStatus}`,
        externalMessage && `${t('header.alerts.deliveryFailed.externalMessage')}: ${externalMessage}`,
    ].filter((part) => !!part);

    const externalUrl = record?.externalUrl || record?.deliveryRecord?.externalUrl;

    // parse stepRun.context json safely to avoid errors
    let context = stepRun?.context;
    try {
        context = JSON.parse(stepRun?.context);
    } catch (e) {
        context = {};
    }
    const stepAlert = context?.alert;

    const { steprunError } = useSteprunInfo(record, stepRun);

    if (!record || !stepRun) return undefined;

    if (isDeleted) {
        // Final exclusive state
        const deletedReason = toHumanReadable(titleCase(record?.deletedReason));
        return {
            title: t('header.alerts.deleted.title', {
                firstName: record?.deletedBy?.firstName,
                lastName: record?.deletedBy?.lastName,
                deletedAt: formatDate(new Date(record?.deletedAt), 'EEEE, do MMMM yyyy, HH:mm'),
                email: record?.deletedBy?.email,
            }),
            message: deletedReason ? t('header.alerts.deleted.reason', { reason: deletedReason }) : undefined,
            severity: 'error',
            bold: true,
        };
    } else if (isDiscarded) {
        // Final exclusive state
        const discardReason = toHumanReadable(titleCase(record.discardRecord?.reason));
        return {
            message: discardReason
                ? t('assistance:discardInfo.text', {
                      date: new Date(record.discardRecord?.createdAt).toLocaleString(),
                      user: record.discardRecord?.user
                          ? `${record.discardRecord.user.firstName} ${record.discardRecord.user.lastName}`
                          : 'Workist',
                      reason: discardReason,
                  })
                : t('assistance:discardInfo.textNoReason', {
                      date: new Date(record.discardRecord?.createdAt).toLocaleString(),
                      user: record.discardRecord?.user
                          ? `${record.discardRecord.user.firstName} ${record.discardRecord.user.lastName}`
                          : 'Workist',
                  }),
            severity: 'info',
            bold: true,
        };
    } else if (isDeliveryFailed) {
        return {
            title: t('header.alerts.deliveryFailed.title'),
            message: `${externalParts.join(' - ') || t('header.alerts.deliveryFailed.noMessage')} \n${t('header.alerts.deliveryFailed.adminNote')}`,
            severity: 'error',
            resolution: ERROR_RESOLUTIONS.Assistance,
            assistanceReason: externalMessage,
        };
    } else if ((isFailed || isRejected) && !isPending) {
        // if it is pending, we don't want to show the error message
        if (steprunError != null) {
            return {
                title: steprunError.title,
                message: steprunError.message,
                resolution: steprunError.resolution,
                severity: 'error',
            };
        }

        return {
            title: t('header.alerts.executionFailed.title'),
            message: t('header.alerts.executionFailed.message'),
            severity: 'error',
        };
    } else if (isSucceeded) {
        // Final exclusive state
        const alert: any = {
            title: t('header.alerts.executionFinished.title'),
            message: '',
            severity: 'success',
        };
        if (record.assistedBy) {
            alert.message = t('header.alerts.executionFinished.messageAssistance', {
                firstName: record.assistedBy.firstName,
                lastName: record.assistedBy.lastName,
                assistedAt: formatDate(new Date(record.assistedAt), 'EEEE, do MMMM yyyy, HH:mm'),
                email: record.assistedBy.email,
            });
        } else if (record.assistedAt) {
            alert.message = t('header.alerts.executionFinished.messageAssistanceDeletedUser', {
                assistedAt: formatDate(new Date(record.assistedAt), 'EEEE, do MMMM yyyy, HH:mm'),
            });
        } else {
            alert.message = t('header.alerts.executionFinished.messageAutomatic');
        }

        if (externalParts.length) {
            alert.message += ` (${externalParts.join(' - ')})`;
        }

        if (externalUrl) {
            alert.resolution = ERROR_RESOLUTIONS.External;
            alert.externalUrl = externalUrl;
        }

        return alert;
    } else if (!isManualOnly) {
        // Intermediate state
        return {
            title: t('header.alerts.noAssistanceNeeded.title'),
            message: t('header.alerts.noAssistanceNeeded.message'),
            severity: 'warning',
        };
    } else if (locked) {
        // Intermediate state
        return {
            title: t('header.alerts.locked.title'),
            message: lockedBy
                ? t('header.alerts.locked.messageWithUser', { email: lockedBy.email })
                : t('header.alerts.locked.message'),
            severity: 'warning',
        };
    } else if (isOnHold) {
        return {
            title: t('header.alerts.onHold.title'),
            message: t('header.alerts.onHold.message', {
                user: `${record.putOnHoldBy.firstName} ${record.putOnHoldBy.lastName}`,
                reason: record.putOnHoldReason || t('header.alerts.onHold.noReason'),
            }),
            severity: 'info',
        };
    } else if (stepAlert) {
        return {
            title: t(`header.alerts.${stepAlert?.name}.title`),
            message: t(`header.alerts.${stepAlert?.name}.message`, stepAlert),
            severity: stepAlert.severity || 'info',
        };
    }
};

const getTabIcon = (tabName: TABS_OPTIONS) => {
    switch (tabName) {
        case TABS_OPTIONS.Source:
            return <SourceIcon />;
        case TABS_OPTIONS.Document:
            return <DocumentIcon />;
        default:
            return <DocumentIcon />;
    }
};

const OnHoldModal = (props) => {
    const { onConfirm, onCancel, ...modalProps } = props;
    const { t } = useTranslation('assistance');

    const [reason, setReason] = useState('');

    return (
        <ConfirmModal
            title={t('onHoldModal.reasonLabel')}
            onConfirm={() => onConfirm(reason)}
            onCancel={onCancel}
            labelCancel={t('onHoldModal.cancel')}
            labelConfirm={t('onHoldModal.discardButton')}
            {...modalProps}
        >
            <TextField
                value={reason}
                onValueChange={(value) => setReason(value)}
                placeholder={t('onHoldModal.reasonPlaceHolder')}
            />
        </ConfirmModal>
    );
};
